/**
 * ThisDeviceCatalogTab — offline product catalog readiness for this computer.
 *
 * TCA-26100804, step 2 of the tab-wise restructure. Display only: no action is
 * available here, so none is offered.
 *
 * The catalog status is fetched here because this is its only consumer. The
 * reference-image readiness is passed in from the panel, which already holds
 * it for the tab summary — a second subscription would duplicate a read for no
 * benefit, which is the pattern recorded for later review in this TCA.
 */
import React, { useEffect, useMemo } from 'react';

import { formatDateTimeWithZone } from '@/utils/dateUtils';
import { catalogMediaReadinessPresentation, inventoryUnread } from '@/utils/mediaReadinessPresentation';
import useDesktopObservation from '@/hooks/useDesktopObservation';
import DeviceStatusSlot, { slotTone } from './DeviceStatusSlot';
import SupportDetails from './SupportDetails';

function catalogPresentation(status, loading, desktopAvailable) {
  if (!desktopAvailable) {
    return { label: 'Desktop app required', detail: 'Offline catalog status is available in the desktop app.', tone: 'neutral' };
  }
  if (loading) return { label: 'Checking', detail: 'Reading protected offline catalog status.', tone: 'neutral' };
  if (status?.state === 'current') {
    return { label: 'Current', detail: 'The protected product catalog is ready for authorized offline work.', tone: 'green' };
  }
  if (status?.state === 'checking' || status?.state === 'preparing') {
    return { label: 'Preparing', detail: 'The protected product catalog is being checked or updated.', tone: 'amber' };
  }
  if (status?.state === 'investigation_required') {
    return { label: 'Needs investigation', detail: 'Catalog integrity needs review. The prior accepted catalog was preserved.', tone: 'red' };
  }
  // Being offline is the feature working, not a fault. The desktop already
  // separates the two causes of a delayed check, and `cloud_unavailable` is
  // set straight from the connectivity signal, so no instruction, attention
  // tone or "could not finish" is warranted. This is the correction TCA-26100804
  // step 7 made for media readiness, applied to the catalogue.
  if (status?.state === 'delayed' && status?.reason === 'cloud_unavailable') {
    return {
      label: 'Check paused',
      detail: 'This computer is offline. The catalog already accepted here is unaffected, and checking resumes when the connection returns.',
      tone: 'neutral',
    };
  }
  if (status?.state === 'delayed') {
    return { label: 'Delayed', detail: 'The catalog check could not finish. Any accepted catalog remains preserved.', tone: 'amber' };
  }
  return status?.state === 'not_available'
    ? { label: 'Not available', detail: 'A complete protected catalog has not yet been accepted on this desktop.', tone: 'neutral' }
    : { label: 'Status unavailable', detail: 'Offline catalog status could not be established.', tone: 'neutral' };
}

export default function ThisDeviceCatalogTab({
  referenceImages = null,
  shopTimezone = null,
  onSummary = null,
}) {
  const observation = useDesktopObservation('getCatalogStatus', 'onCatalogStatusChanged');
  const { status, loading, available: desktopAvailable } = observation;

  const view = useMemo(
    () => catalogPresentation(status, loading, desktopAvailable),
    [status, loading, desktopAvailable],
  );

  // Report the catalogue's own state upward so the tab summary names the same
  // subject the tab does. Reported from what is already read here; a second
  // reader would duplicate this subscription (TCA-26100804).
  useEffect(() => {
    if (!onSummary) return;
    onSummary(loading || !desktopAvailable || !status
      ? null
      : { label: view.label, tone: slotTone(view.tone), attention: status.state === 'investigation_required' });
    return () => onSummary(null);
    // The dependency list is exact now that the tone map is no longer rebuilt
    // on every render; the suppression it needed is gone with it.
  }, [onSummary, loading, desktopAvailable, status, view.label, view.tone]);

  // Reference images read as a line under the catalog status, not as a second
  // status: they are part of the catalog being ready, not a separate subject.
  const images = useMemo(() => {
    // A null status is the unread state; a separate loading flag said the
    // same thing and no screen set it (TCA-26100804 closing audit).
    if (!referenceImages) return null;
    const presentation = catalogMediaReadinessPresentation(referenceImages);
    return { ...presentation, counts: presentation.valid ? referenceImages.counts : null };
  }, [referenceImages]);

  const lines = [];
  if (images) {
    // A count is only meaningful once the inventory has been read. While it is
    // being checked, printing it would state a zero nobody has established
    // (TCA-26100804 closeout).
    const unread = inventoryUnread(referenceImages);
    lines.push(images.counts && !unread
      ? `${images.label}: ${images.counts.available.toLocaleString()} of ${images.counts.eligible.toLocaleString()} available offline`
      : images.label);
    // The cause sentence is the troubleshooting value the readiness work added
    // (TCA-26100708), so it must survive the tidier layout. It is shown only
    // when the images are not current: in the healthy state it merely repeats
    // the count line above it.
    if (referenceImages.state !== 'current' && images.detail && images.detail !== images.label) {
      lines.push(images.detail);
    }
  }

  const stamp = (value) => (value ? formatDateTimeWithZone(value, shopTimezone) || null : null);

  return (
    <div className="space-y-3" data-testid="this-device-catalog-tab">
      <DeviceStatusSlot
        tone={slotTone(view.tone)}
        title={view.label}
        detail={view.detail}
        lines={lines}
        testId="offline-catalog-slot"
      />
      <SupportDetails
        testId="catalog-support-details"
        rows={[
          ['Last confirmed current', stamp(status?.lastCurrentAt)],
          ['Last catalog check', stamp(status?.lastCheckAt)],
          ['Reference images available', images?.counts && !inventoryUnread(referenceImages)
            ? images.counts.available.toLocaleString() : null],
          ['Reference images eligible', images?.counts && !inventoryUnread(referenceImages)
            ? images.counts.eligible.toLocaleString() : null],
        ]}
      />
    </div>
  );
}
